import React from 'react';
import LinearText from '../linear-text/linear-text';
import {twMerge} from 'tailwind-merge';
import cn from 'classnames';
interface CtaButtonProps {
    text: string | React.ReactNode;
    loadingText?: string | React.ReactNode; // Thêm prop cho text loading
    onClick?: () => void;
    className?: string;
    contentClassName?: string;
    contentContainerClassName?: string;
    type?: 'button' | 'submit' | 'reset';
    disable?: boolean;
    loading?: boolean;
}

export const CtaButton: React.FC<CtaButtonProps> = ({
    text,
    loadingText,
    onClick,
    className = '',
    contentClassName = '',
    contentContainerClassName = '',
    type = 'button',
    disable = false,
    loading = false,
}) => {
    const outerGradientStyle = {
        background:
            'linear-gradient(290.93deg, #DABA03 18.72%, #FFF04D 37.08%, #FFFDBD 43.79%, #CEAF00 57.39%, #FFFDBD 66.64%, #FFE659 77.01%)',
        WebkitBackground:
            'linear-gradient(290.93deg, #DABA03 18.72%, #FFF04D 37.08%, #FFFDBD 43.79%, #CEAF00 57.39%, #FFFDBD 66.64%, #FFE659 77.01%)',
    };

    const innerGradientStyle = {
        background: 'linear-gradient(103.28deg, #E6FFFD 1.06%, #F6E989 95.93%)',
        WebkitBackground: 'linear-gradient(103.28deg, #E6FFFD 1.06%, #F6E989 95.93%)',
    };

    const style = twMerge(`px-1 py-1 rounded-full cursor-pointer flex justify-center items-center`, className);
    const contentStyle = twMerge(`py-2.5 font-bold`, contentClassName);
    const contentContainerStyle = twMerge(
        `h-full w-full rounded-full flex justify-center items-center`,
        contentContainerClassName,
    );

    const isDisabled = disable || loading;

    return (
        <button
            type={type}
            disabled={isDisabled}
            onClick={onClick}
            aria-label="Cta Button"
            className={cn(style, 'relative overflow-hidden', {
                'cursor-not-allowed': isDisabled,
                'transform shadow-[0px_15px_13px_0px_rgba(0,30,183,0.5)] transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-xl hover:shadow-yellow-300/60':
                    !isDisabled,
            })}
            style={{
                ...outerGradientStyle,
                // Chỉ làm mờ nhẹ khi disable, không quá tối
                opacity: isDisabled ? 1 : 1,
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
            }}>
            {/* Hiệu ứng ánh sáng chạy dọc theo border - làm mờ nhẹ khi disable */}
            {loading && (
                <div className="absolute inset-0 rounded-full" style={{opacity: isDisabled ? 0.8 : 1}}>
                    {/* Top border light */}
                    <div
                        className="absolute top-0 left-0 h-1 rounded-full"
                        style={{
                            width: '100%',
                            background: 'linear-gradient(90deg, transparent, #ffffff, #ffff00, transparent)',
                            animation: 'flowRight 2s ease-in-out infinite',
                            opacity: 0.9, // Làm mờ nhẹ ánh sáng để phù hợp disable state
                        }}
                    />
                    {/* Right border light */}
                    <div
                        className="absolute top-0 right-0 w-1 rounded-full"
                        style={{
                            height: '100%',
                            background: 'linear-gradient(180deg, transparent, #ffffff, #ffff00, transparent)',
                            animation: 'flowDown 2s ease-in-out infinite 0.5s',
                            opacity: 0.9,
                        }}
                    />
                    {/* Bottom border light */}
                    <div
                        className="absolute bottom-0 right-0 h-1 rounded-full"
                        style={{
                            width: '100%',
                            background: 'linear-gradient(270deg, transparent, #ffffff, #ffff00, transparent)',
                            animation: 'flowLeft 2s ease-in-out infinite 1s',
                            opacity: 0.9,
                        }}
                    />
                    {/* Left border light */}
                    <div
                        className="absolute top-0 left-0 w-1 rounded-full"
                        style={{
                            height: '100%',
                            background: 'linear-gradient(0deg, transparent, #ffffff, #ffff00, transparent)',
                            animation: 'flowUp 2s ease-in-out infinite 1.5s',
                            opacity: 0.9,
                        }}
                    />
                </div>
            )}

            <div
                className={cn(contentContainerStyle, 'relative z-10')}
                style={{
                    ...innerGradientStyle,
                    // Làm mờ nhẹ inner gradient khi disable
                    opacity: isDisabled ? 0.9 : 1,
                }}>
                <div className="flex items-center justify-center gap-2">
                    {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    )}
                    <LinearText className={contentStyle}>{loading ? loadingText || 'ĐANG XỬ LÝ...' : text}</LinearText>
                </div>

                {/* CSS keyframes cho border flowing effect */}
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
            @keyframes flowRight {
              0% { transform: translateX(-100%); opacity: 0; }
              50% { opacity: 1; }
              100% { transform: translateX(100%); opacity: 0; }
            }
            @keyframes flowDown {
              0% { transform: translateY(-100%); opacity: 0; }
              50% { opacity: 1; }
              100% { transform: translateY(100%); opacity: 0; }
            }
            @keyframes flowLeft {
              0% { transform: translateX(100%); opacity: 0; }
              50% { opacity: 1; }
              100% { transform: translateX(-100%); opacity: 0; }
            }
            @keyframes flowUp {
              0% { transform: translateY(100%); opacity: 0; }
              50% { opacity: 1; }
              100% { transform: translateY(-100%); opacity: 0; }
            }
          `,
                    }}
                />
            </div>
        </button>
    );
};
